import { replace, LOCATION_CHANGE } from 'react-router-redux';
import { handleActions } from 'redux-actions';
import { getFormValues } from 'redux-form';
import moment from 'moment';
import qs from 'qs';

import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';

import checkPermissions from '../shared/check-permissions';
import http from '../shared/http';
import endpoints from '../../config/endpoints';
import intl from '../shared/intl';

import createCalendar, { getDateRangeFromCalendar } from '../shared/calendar';
import createFilters from '../shared/filters';
import createTable from './components/table';

const calendar = createCalendar({
	namespace: 'affiliatesRedeem',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(14, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace: 'affiliatesRedeem',
	stateNodeName: 'filters',
	listOfFilters: [
		{
			name: 'offers',
			title: 'Offers',
			placeholder: 'Add offers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS
			}
		},
		{
			name: 'affiliates',
			title: 'Affiliates',
			placeholder: 'Add affiliates',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.AFFILIATES
			}
		}
	]
});

const table = createTable({
	namespace: 'affiliatesRedeem',
	stateNodeName: 'table'
});

// actions types
const FETCH_DATA_SUCCESS = 'affiliates-redeem/fetch-data-success';
const FETCH_DATA_ERROR = 'affiliates-redeem/fetch-data-error';

const TOGGLE_PROGRESS_BAR = 'affiliates-redeem/toggle-progress-bar';

const APPLY_LOCATION = 'affiliates-redeem/apply-location';
const APPLY = 'affiliates-redeem/apply';
const CLEAR = 'affiliates-redeem/clear';

const TOGGLE_ALL_ROWS = 'affiliates-redeem/toggle-all-rows';
const TOGGLE_ONE_ROW = 'affiliates-redeem/toggle-one-row';

const LOCATIONS_LOOKUP_SUCCESS = 'affiliates-redeem/locations-lookup-success';
const LOCATIONS_LOOKUP_ERROR = 'affiliates-redeem/locations-lookup-error';

// permissions
export const allowedPermissions = {
	view: 'affiliates_redeem_list'
};

// initial state
const initialState = {
	data: null,
	calendar: calendar.initialState,
	showProgressBar: false,
	filters: filters.initialState,
	table: table.initialState,
	openedAffiliateIds: [],
	query: {
		date_range: {
			start: null,
			end: null
		},
		conversions_from: 1
	},
	locationFilterValues: [],
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: props => (dispatch, getState) => {
			const externalQuery = (props && props.location && props.location.query && qs.parse(props.location.query)) || {};
			const savedFilters = filters.selectors.getSavedFilters(getState(), 'affiliatesRedeem');

			if (isEmpty(externalQuery) && savedFilters.entity && savedFilters.entity.filters.length) {
				return dispatch(replace(`affiliates-redeem/?${qs.stringify(savedFilters)}`));
			}
			if (document.isLoading) {
				return;
			}

			document.isLoading = true;

			return request(getState, dispatch, externalQuery).then(() => {
				document.isLoading = false;

				if (externalQuery.entity) {
					const filtersList = externalQuery.entity.filters || [];
					dispatch(filters.actions.setFiltersFromExternalQuery(filtersList));
				}
			});
		},

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		}),

		applyLocation: (value) => ({
			type: APPLY_LOCATION,
			payload: value
		}),

		apply: () => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());
			const formValues = getFormValues('conversionsFromAffiliatesRedeem')(getState());
			setTimeout(() => dispatch({
				type: APPLY,
				payload: formValues
			}), 100);
		},

		clear: () => dispatch => {
			dispatch({ type: CLEAR });
			setTimeout(() => dispatch(replace('/affiliates-redeem/')), 100);
		},

		toggleAllRows: () => ({
			type: TOGGLE_ALL_ROWS
		}),

		toggleOneRow: affiliateId => ({
			type: TOGGLE_ONE_ROW,
			payload: affiliateId
		}),

		locationsLookup: text => (dispatch, getState) => {
			const request = http({
				url: `${endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC}?query=${text}`,
				method: 'get',
				actions: [LOCATIONS_LOOKUP_SUCCESS, LOCATIONS_LOOKUP_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === LOCATIONS_LOOKUP_SUCCESS) {
					return { options: action.payload.data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		}
	},
	calendar.actions,
	filters.actions,
	table.actions
);

// reducers
const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: state => ({
			...initialState
		}),
		[FETCH_DATA_SUCCESS]: (state, action) => {
			return {
				...state,
				data: action.payload.data,
				query: {
					...state.query,
					date_range: getDateRangeFromCalendar(state.calendar)
				},
				showProgressBar: false,
				error: false
			};
		},
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[APPLY_LOCATION]: (state, action) => ({
			...state,
			locationFilterValues: action.payload
		}),
		[APPLY]: (state, action) => {
			const { conversionsFrom } = action.payload;

			return {
				...state,
				showProgressBar: false,
				query: {
					...state.query,
					conversions_from: conversionsFrom ? conversionsFrom : 0
				},
				data: null
			};
		},
		[CLEAR]: state => ({
			...state,
			query: {
				...initialState.query
			}
		}),
		[TOGGLE_ALL_ROWS]: state => {
			const { data, openedAffiliateIds } = state;
			return {
				...state,
				openedAffiliateIds: openedAffiliateIds.length ? [] : data.map(row => row.affiliate_id)
			};
		},
		[TOGGLE_ONE_ROW]: (state, action) => ({
			...state,
			openedAffiliateIds:
				state.openedAffiliateIds.indexOf(action.payload) !== -1
					? state.openedAffiliateIds.filter(i => i !== action.payload)
					: state.openedAffiliateIds.concat([action.payload])
		}),
		[LOCATIONS_LOOKUP_ERROR]: state => ({
			...state,
			progressBar: false,
			error: true
		}),
		[FETCH_DATA_ERROR]: state => ({
			...state,
			progressBar: false,
			error: true
		})
	},
	calendar.reducers,
	filters.reducers,
	table.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.affiliatesRedeem.data,
	getTable: table.selectors.getTable,
	getProgressBar: state => state.affiliatesRedeem.showProgressBar,
	getIntl: state => intl(state),
	getCan: state => checkPermissions(state, allowedPermissions),
	getCalendar: state => calendar.selectors.getCalendar(state => state.affiliatesRedeem.calendar, state => state)(state),
	getFilters: state => filters.selectors.getFilters(state => state.affiliatesRedeem.filters, state => state)(state),
	getConversionsFilterInitialValues: state => ({
		conversionsFrom: state.affiliatesRedeem.query.conversions_from
	}),
	getCurrentDateRange: state => state.affiliatesRedeem.query.date_range,
	getOpenedAffiliateIds: state => state.affiliatesRedeem.openedAffiliateIds,
	getLocationFilterValues: state => state.affiliatesRedeem.locationFilterValues,
	getError: state => state.affiliatesRedeem.error
};

// functions
const getRequestQuery = (state, externalQuery = {}) => {
	const {
		query,
		calendar,
		filters: { selectedFilters },
		locationFilterValues
	} = cloneDeep(state.affiliatesRedeem);

	const dateRange = getDateRangeFromCalendar(calendar);

	if (dateRange) {
		query['date_range'] = dateRange;
	}

	if (selectedFilters) {
		const filters = {};
		const inverseFilters = {};

		selectedFilters.forEach(filter => {
			const values = filter.value.map(f => f.value);
			if (values.length) {
				const selectedFilterValues = filter.shouldInverse ? inverseFilters : filters;
				selectedFilterValues[filter.name] = values;
			}
		});

		if (!isEmpty(filters)) {
			query['filters'] = filters;
		}

		if (!isEmpty(inverseFilters)) {
			query['inverse_filters'] = inverseFilters;
		}
	}

	if (locationFilterValues) {
		query['filters'] = { ...query.filters, countries: locationFilterValues.map(item => item.value) };
	}

	return qs.stringify({ ...query, ...externalQuery });
};

const request = (getState, dispatch, externalQuery) => {
	const query = getRequestQuery(getState(), externalQuery && externalQuery.query);
	return http({
		url: `${endpoints.AFFILIATES.REDEEM}?${query}`,
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR]
	})(dispatch);
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/affiliates-redeem/index.js